import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/marketplaceTerms";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export const ReconciliationQueue = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/payments/reconciliation").then(({ data }) => setRows(data.bookings)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const cancel = async (r) => {
    if (!window.confirm(`Cancel ${r.booking_id} as a no-funds artifact? No emails or refunds are triggered.`)) return;
    setBusy(r.booking_id);
    try {
      await api.post(`/admin/payments/reconciliation/${r.booking_id}/cancel`, { reason: "legacy mock-checkout test artifact" });
      toast.success(`${r.booking_id} cancelled — removed from reconciliation.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not cancel booking");
    } finally {
      setBusy(null);
    }
  };

  if (!rows || rows.length === 0) return null;
  return (
    <div className="mb-6 card-elegant p-5 border-[#E8B96A]" data-testid="reconciliation-queue">
      <div className="eyebrow text-[#9A6B00]">Needs reconciliation · {rows.length}</div>
      <p className="text-xs text-[color:var(--text-muted)] mt-1">
        Marked paid with no payment records. Balance checkout, reminders and completion are blocked until an admin reconciles each one.
      </p>
      <ul className="mt-3 divide-y divide-[color:var(--border)] max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.booking_id} className="py-2 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4" data-testid={`reconcile-row-${r.booking_id}`}>
            <a href={`/bookings/${r.booking_id}`} className="font-mono text-xs link-underline" data-testid={`reconcile-link-${r.booking_id}`}>{r.booking_id}</a>
            <span className="flex-1 truncate">{r.vendor_name} × {r.planner_name} · event {fmt(r.date)}</span>
            <span className="font-semibold whitespace-nowrap">{formatUSD(r.quote_amount)}</span>
            <span className="text-[11px] text-[color:var(--text-muted)] whitespace-nowrap" data-testid={`reconcile-origin-${r.booking_id}`}>
              {r.likely_origin === "legacy_mock_checkout" ? "Legacy mock checkout — no real charge" : "Status set manually"}
              {r.reminders_sent?.length > 0 && ` · ${r.reminders_sent.length} reminder(s) sent`}
            </span>
            <button
              type="button"
              onClick={() => cancel(r)}
              disabled={busy === r.booking_id}
              className="chip text-[11px] whitespace-nowrap disabled:opacity-50"
              data-testid={`reconcile-cancel-${r.booking_id}`}
            >{busy === r.booking_id ? "Cancelling…" : "Cancel (no funds)"}</button>
          </li>
        ))}
      </ul>
    </div>
  );
};
